import BaseApiService from './BaseApiService.js';

class AcademicSectionService extends BaseApiService {

  // ✅ Get all academic sections for the authenticated organization
  async getAllSections(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/api/academic-sections/all${queryString ? `?${queryString}` : ''}`;
      const response = await this.makeRequest(endpoint, { method: 'GET' });
      return response && response.sections ? response.sections : (Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('❌ Failed to fetch academic sections:', error);
      throw error;
    }
  }

  // ✅ Get statistics for degree college academic structure
  async getStats() {
    try {
      const response = await this.makeRequest('/api/academic-sections/stats', { method: 'GET' });
      return response && response.stats ? response.stats : (response || {});
    } catch (error) {
      console.error('❌ Failed to fetch academic section stats:', error);
      return {
        totalDepartments: 0,
        totalPrograms: 0,
        totalSemesters: 0,
        totalSections: 0,
        totalStudents: 0,
        activeFaculty: 0,
        availableSections: 0
      };
    }
  }

  // ✅ Create a new academic section
  async createSection(sectionData) {
    if (!sectionData.departmentId) throw new Error('Department is required');
    if (!sectionData.programId) throw new Error('Program / Course is required');
    if (!sectionData.semester) throw new Error('Semester is required (e.g. Sem 1)');
    if (!sectionData.sectionName) throw new Error('Section name is required (e.g. Section A)');

    const payload = {
      academicYear: sectionData.academicYear || '2026-2027',
      departmentId: Number(sectionData.departmentId),
      programId: Number(sectionData.programId),
      semesterId: sectionData.semesterId ? Number(sectionData.semesterId) : null,
      semester: sectionData.semester.trim(),
      sectionName: sectionData.sectionName.trim(),
      facultyAdvisorId: sectionData.facultyAdvisorId ? String(sectionData.facultyAdvisorId) : null,
      facultyAdvisorName: sectionData.facultyAdvisorName || null,
      roomNumber: sectionData.roomNumber ? sectionData.roomNumber.trim() : 'Room 101',
      maxCapacity: sectionData.maxCapacity ? Number(sectionData.maxCapacity) : 60,
      status: sectionData.status || 'ACTIVE'
    };

    console.log('📤 Creating academic section with payload:', payload);
    return this.makeRequest('/api/academic-sections/create', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // ✅ Bulk generate semesters and sections for a program
  async bulkGenerate(bulkData) {
    if (!bulkData.departmentId) throw new Error('Department is required');
    if (!bulkData.programId) throw new Error('Program / Course is required');

    const payload = {
      academicYear: bulkData.academicYear || '2026-2027',
      departmentId: Number(bulkData.departmentId),
      programId: Number(bulkData.programId),
      numberOfSemesters: Number(bulkData.numberOfSemesters || 6),
      sectionNames: bulkData.sectionNames || ['Section A', 'Section B'],
      maxCapacity: Number(bulkData.maxCapacity || 60),
      defaultRoomPrefix: bulkData.defaultRoomPrefix || 'Room '
    };

    console.log('⚡ Bulk generating academic sections:', payload);
    return this.makeRequest('/api/academic-sections/bulk-generate', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // ✅ Update section
  async updateSection(id, sectionData) {
    return this.makeRequest(`/api/academic-sections/update/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sectionData)
    });
  }

  // ✅ Delete section
  async deleteSection(id) {
    return this.makeRequest(`/api/academic-sections/delete/${id}`, {
      method: 'DELETE'
    });
  }
}

const academicSectionService = new AcademicSectionService();
export default academicSectionService;
